import { useEffect, useRef } from "react";


const ALLOWED_TAGS = new Set([
    "B", "STRONG", "I", "EM", "P", "BR", "DIV", "SPAN",
    "TABLE", "THEAD", "TBODY", "TR", "TH", "TD", "UL", "OL", "LI",
]);

const escapeHtml = (value) => String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

export function sanitizeQuestionHtml(value) {
    const source = String(value || "");
    if (typeof document === "undefined") return escapeHtml(source).replace(/\r?\n/g, "<br>");
    if (!/<[a-z][\s\S]*>/i.test(source)) return escapeHtml(source).replace(/\r?\n/g, "<br>");
    const template = document.createElement("template");
    template.innerHTML = source;
    const clean = (parent) => {
        [...parent.childNodes].forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            if (!ALLOWED_TAGS.has(node.tagName)) {
                if (["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED"].includes(node.tagName)) {
                    node.remove();
                    return;
                }
                clean(node);
                node.replaceWith(...node.childNodes);
                return;
            }
            [...node.attributes].forEach((attribute) => node.removeAttribute(attribute.name));
            clean(node);
        });
    };
    clean(template.content);
    return template.innerHTML;
}

export function QuestionContent({ value, className = "" }) {
    return (
        <div
            className={`${className} overflow-x-auto [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-slate-300 [&_td]:p-2 [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-50 [&_th]:p-2`}
            dangerouslySetInnerHTML={{ __html: sanitizeQuestionHtml(value) }}
        />
    );
}

export function RichQuestionEditor({ value, onChange, autoFocus = false }) {
    const editorRef = useRef(null);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        const safeValue = sanitizeQuestionHtml(value);
        if (editor.innerHTML !== safeValue) editor.innerHTML = safeValue;
    }, [value]);

    useEffect(() => {
        if (autoFocus) editorRef.current?.focus();
    }, [autoFocus]);

    const applyCommand = (command) => {
        editorRef.current?.focus();
        document.execCommand?.(command, false);
        onChange(editorRef.current?.innerHTML || "");
    };

    const insertTable = () => {
        const rows = Number(window.prompt("Количество строк (1–10)", "3"));
        if (!rows) return;
        const columns = Number(window.prompt("Количество столбцов (1–6)", "3"));
        if (!columns) return;
        if (rows < 1 || rows > 10 || columns < 1 || columns > 6) {
            window.alert("Допустимо от 1 до 10 строк и от 1 до 6 столбцов.");
            return;
        }
        const table = `<table><tbody>${Array.from({ length: rows }, () => (
            `<tr>${Array.from({ length: columns }, () => "<td>Данные</td>").join("")}</tr>`
        )).join("")}</tbody></table><p><br></p>`;
        editorRef.current?.focus();
        document.execCommand?.("insertHTML", false, table);
        onChange(editorRef.current?.innerHTML || "");
    };

    const editTable = (action) => {
        const editor = editorRef.current;
        if (!editor) return;
        const selection = window.getSelection?.();
        const selectedNode = selection?.rangeCount ? selection.getRangeAt(0).startContainer : null;
        const selectedElement = selectedNode?.nodeType === Node.ELEMENT_NODE
            ? selectedNode
            : selectedNode?.parentElement;
        const table = selectedElement?.closest?.("table") || editor.querySelector("table");
        if (!table || !table.rows.length) {
            window.alert("Сначала вставьте таблицу или выберите её ячейку.");
            return;
        }
        if (action === "row-add") {
            const row = table.insertRow();
            for (let index = 0; index < table.rows[0].cells.length; index += 1) row.insertCell().textContent = "Данные";
        } else if (action === "column-add") {
            [...table.rows].forEach((row) => row.insertCell().textContent = "Данные");
        } else if (action === "row-remove") {
            if (table.rows.length === 1) table.remove();
            else table.deleteRow(table.rows.length - 1);
        } else if (action === "column-remove") {
            if (table.rows[0].cells.length === 1) table.remove();
            else [...table.rows].forEach((row) => row.deleteCell(row.cells.length - 1));
        }
        onChange(editor.innerHTML);
    };

    return (
        <div className="mb-4 overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-[#cda834] focus-within:ring-2 focus-within:ring-[#cda834]/20">
            <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-1.5">
                <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("bold")} className="rounded px-2.5 py-1 text-sm font-bold text-slate-700 hover:bg-white" aria-label="Полужирный текст">B</button>
                <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyCommand("italic")} className="rounded px-2.5 py-1 text-sm italic text-slate-700 hover:bg-white" aria-label="Курсивный текст">I</button>
                <button type="button" onClick={insertTable} className="rounded px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-white">Вставить таблицу</button>
                <button type="button" onClick={() => editTable("row-add")} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">Строка +</button>
                <button type="button" onClick={() => editTable("column-add")} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">Столбец +</button>
                <button type="button" onClick={() => editTable("row-remove")} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">Строка −</button>
                <button type="button" onClick={() => editTable("column-remove")} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">Столбец −</button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Текст вопроса"
                data-testid="test-question-rich-editor"
                onInput={(event) => onChange(event.currentTarget.innerHTML)}
                className="min-h-[88px] px-3 py-2 text-sm leading-6 text-slate-900 outline-none [&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-slate-300 [&_td]:p-2 [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-50 [&_th]:p-2"
            />
        </div>
    );
}
